// frontend/src/pages/Status.js
import React, { useEffect, useState } from "react";
import "./Complemento.css";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";

import ThemeProvider from "../theme/TemaMuiProvider";
import { Box, Button, TextField, TablePagination, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import muiTheme from "../theme/muiTheme";

function Status() {
  const [statusList, setStatusList] = useState([]);
  const [filtro, setFiltro] = useState("");
  const [novoStatus, setNovoStatus] = useState("");

  const [editandoId, setEditandoId] = useState(null);
  const [nomeEditado, setNomeEditado] = useState("");

  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(5);

  // ✅ minimizar/expandir grid (persistido)
  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("statusGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("statusGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  // =======================
  // SISTEMA DE PERMISSÕES
  // =======================
  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    const decoded = jwtDecode(token);
    tipoUsuario = decoded.tipo
      ?.toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);

  // =======================
  // Buscar status
  // =======================
  useEffect(() => {
    if (!podeVisualizar) return;

    const tk = localStorage.getItem("token");
    fetch("/api/status", {
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const lista = Array.isArray(data) ? data : [];
        const normalizada = lista.map((s) => ({
          id: s?.id,
          nome: (s?.nome ?? "").toString(),
        }));
        setStatusList(normalizada);
      })
      .catch((err) => console.error("Erro ao buscar status:", err));
  }, [podeVisualizar]);

  // =======================
  // Criar status (MAIÚSCULO só ao gravar)
  // =======================
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!podeCadastrar) {
      alert("Você não tem permissão para cadastrar status.");
      return;
    }

    const tk = localStorage.getItem("token");

    const digitado = (novoStatus || "").trim();
    const nomeUpper = digitado.toUpperCase(); // ✅ converte apenas no envio

    if (!digitado) {
      alert("Digite um status.");
      return;
    }

    // evita duplicado na lista local (comparando em MAIÚSCULO)
    if (statusList.some((s) => (s.nome || "").trim().toUpperCase() === nomeUpper)) {
      alert("Status já cadastrado.");
      return;
    }

    fetch("/api/status", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tk}`,
      },
      body: JSON.stringify({ nome: nomeUpper }),
    })
      .then(async (res) => {
        if (res.status === 403) {
          alert("Sem permissão para cadastrar.");
          return null;
        }

        const data = await res.json();

        if (!res.ok) {
          alert(data?.erro || "Erro ao cadastrar status.");
          return null;
        }

        return data;
      })
      .then((data) => {
        if (!data?.id) return;

        // garante que aparece em maiúsculo na lista
        const item = { id: data.id, nome: (data.nome ?? nomeUpper).toString().toUpperCase() };

        setStatusList((prev) => [item, ...prev]);

        setPaginaAtual(1);

        setNovoStatus("");
      })
      .catch((err) => console.error("Erro ao cadastrar status:", err));
  };

  // =======================
  // Entrar em modo edição
  // =======================
  const handleEdit = (item) => {
    if (!podeEditar) {
      alert("Você não tem permissão para editar.");
      return;
    }
    setEditandoId(item.id);
    // mantém como está na UI (que já deve vir em maiúsculo do backend)
    setNomeEditado(item.nome);
  };

  // =======================
  // Salvar edição (MAIÚSCULO só ao gravar)
  // =======================
  const handleSaveEdit = (id) => {
    if (!podeEditar) {
      alert("Você não tem permissão para editar.");
      return;
    }

    const tk = localStorage.getItem("token");

    const digitado = (nomeEditado || "").trim();
    const novoNomeUpper = digitado.toUpperCase(); // ✅ converte apenas no envio

    if (!digitado) {
      alert("Nome inválido.");
      return;
    }

    // evita duplicado local (comparando em MAIÚSCULO e ignorando o próprio id)
    const duplicado = statusList.some((s) => s.id !== id && (s.nome || "").trim().toUpperCase() === novoNomeUpper);

    if (duplicado) {
      alert("Já existe um status com esse nome.");
      return;
    }

    fetch(`/api/status/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tk}`,
      },
      body: JSON.stringify({ nome: novoNomeUpper }),
    })
      .then(async (res) => {
        if (res.status === 403) {
          alert("Sem permissão para editar.");
          return null;
        }

        const data = await res.json();

        if (!res.ok) {
          alert(data?.erro || "Erro ao editar status.");
          return null;
        }

        return data;
      })
      .then((data) => {
        if (!data) return;

        // garante que aparece em maiúsculo na lista
        setStatusList((prev) => prev.map((s) => (s.id === id ? { ...s, nome: novoNomeUpper } : s)).sort((a, b) => (a.nome || "").localeCompare(b.nome || "")));

        setEditandoId(null);
        setNomeEditado("");
      })
      .catch((err) => console.error("Erro ao editar status:", err));
  };

  // =======================
  // Excluir
  // =======================
  const handleDelete = (id) => {
    if (!podeExcluir) {
      alert("Você não tem permissão para excluir.");
      return;
    }

    const confirmar = window.confirm("Deseja realmente excluir este status?");
    if (!confirmar) return;

    const tk = localStorage.getItem("token");
    fetch(`/api/status/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then(async (res) => {
        if (res.status === 403) {
          alert("Sem permissão para excluir.");
          return;
        }

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          alert(data?.erro || "Erro ao excluir status.");
          return;
        }

        setStatusList((prev) => prev.filter((s) => s.id !== id));
      })
      .catch((err) => console.error("Erro ao excluir status:", err));
  };

  // =======================
  // Filtrar
  // =======================
  const statusFiltrados = (statusList || []).filter((s) => (s.nome || "").toLowerCase().includes((filtro || "").toLowerCase()));

  // =======================
  // Paginação
  // =======================
  const indexInicial = (paginaAtual - 1) * itensPorPagina;
  const indexFinal = indexInicial + itensPorPagina;
  const statusPaginados = statusFiltrados.slice(indexInicial, indexFinal);
  const totalPaginas = Math.ceil(statusFiltrados.length / itensPorPagina) || 1;

  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(1);
  }, [paginaAtual, totalPaginas]);

  // =======================
  // Copiar lista (filtrada inteira)
  // =======================
  const handleCopiar = async () => {
    const texto = (statusFiltrados || [])
      .map((s) => s?.nome ?? "")
      .filter(Boolean)
      .join(", ");

    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }

    const ok = await copiarTexto(texto);
    alert(ok ? "Status copiados!" : "Não foi possível copiar automaticamente.");
  };

  // =======================
  // CSV (exporta filtrada inteira)
  // =======================
  const handleCSV = () => {
    const BOM = "\uFEFF";
    const header = ["Status"];

    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const linhas = (statusFiltrados || []).map((s) => [s.nome ?? ""].map(csvEscape));
    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");

    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "status.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="complemento-container">
        <h2>Status</h2>

        {/* FILTRO + BOTÕES (sempre visíveis) */}
        <div className="bloco-linha">
          <TextField
            label="Buscar Status"
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setPaginaAtual(1);
            }}
            fullWidth
            size="small"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon fontSize="small" sx={{ color: "var(--ig-t-9ca3af)" }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 52,
                display: "flex",
                alignItems: "center",
              },
              "& .MuiOutlinedInput-input": {
                padding: "14px 14px",
              },
              "& .MuiInputLabel-root": {
                transform: "translate(14px, 15px) scale(1)",
              },
              "& .MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            }}
          />

          <div className="filtro-botoes">
            <Button
              size="small"
              variant="outlined"
              type="button"
              onClick={() => {
                setFiltro("");
                setPaginaAtual(1);
              }}
              sx={{
                height: 52,
                color: "#2196f3",
                borderColor: "#2196f3",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#42a5f5",
                  backgroundColor: "rgba(33, 150, 243, 0.08)",
                },
              }}
            >
              Limpar Filtro
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCopiar}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              <i className="fas fa-copy" style={{ marginRight: 6 }}></i>
              Copiar
            </Button>

            <Button
              size="small"
              variant="contained"
              type="button"
              onClick={handleCSV}
              sx={{
                height: 52,
                backgroundColor: "#2196f3",
                color: "#fff",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#1976d2",
                },
              }}
            >
              CSV
            </Button>
          </div>
        </div>

        {/* ✅ MINIMIZAR/EXPANDIR (sempre visível) */}
        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button
              type="button"
              className="btn-grid-toggle small danger"
              onClick={() => setGridMinimizado(true)}
              title="Minimizar o Grid"
              aria-label="Minimizar o Grid"
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              className="btn-grid-toggle small outline"
              onClick={() => setGridMinimizado(false)}
              title="Expandir o Grid"
              aria-label="Expandir o Grid"
            >
              ↗ Expandir
            </button>
          )}
        </div>

        {/* GRID (tabela + paginação) */}
        {!gridMinimizado && (
          <div
            className="bloco-lista"
            style={{
              width: "100%",
              maxWidth: "100%",
              overflowX: "auto",
              boxSizing: "border-box",
            }}
          >
            <table style={{ minWidth: "760px" }}>
              <thead>
                <tr>
                  <th>Status</th>
                  <th style={{ width: "300px", textAlign: "center" }}>Ações</th>
                </tr>
              </thead>

              <tbody>
                {statusPaginados.map((item) => (
                  <tr key={item.id}>
                    <td className="linha-cidade">
                      {editandoId === item.id ? (
                        <input value={nomeEditado} onChange={(e) => setNomeEditado(e.target.value)} className="campo-formulario-padrao" />
                      ) : (
                        item.nome
                      )}
                    </td>

                    <td
                      className="celula-acoes"
                      style={{
                        width: "300px",
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <div className="actions-cell">
                        {editandoId === item.id ? (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<i className="fas fa-save" />}
                            onClick={() => handleSaveEdit(item.id)}
                            sx={{
                              minWidth: 120,
                              height: 38,
                              textTransform: "none",
                              fontWeight: 600,
                              backgroundColor: "#2e7d32",
                              "&:hover": {
                                backgroundColor: "#1b5e20",
                              },
                            }}
                          >
                            Salvar
                          </Button>
                        ) : (
                          <>
                            {podeEditar && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<i className="fas fa-pen-to-square" />}
                                onClick={() => handleEdit(item)}
                                sx={{
                                  minWidth: 110,
                                  height: 38,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  backgroundColor: "#1998a8",
                                  "&:hover": {
                                    backgroundColor: "#177f8d",
                                  },
                                }}
                              >
                                Editar
                              </Button>
                            )}

                            {podeExcluir && (
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<i className="fas fa-trash" />}
                                onClick={() => handleDelete(item.id)}
                                sx={{
                                  minWidth: 110,
                                  height: 38,
                                  textTransform: "none",
                                  fontWeight: 600,
                                  backgroundColor: "#d32f2f",
                                  "&:hover": {
                                    backgroundColor: "#b71c1c",
                                  },
                                }}
                              >
                                Excluir
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}

                {statusPaginados.length === 0 && (
                  <tr>
                    <td colSpan={2} style={{ textAlign: "center", padding: "14px" }}>
                      Nenhum status encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* PAGINAÇÃO */}
            <TablePagination
              component="div"
              count={statusFiltrados.length}
              page={paginaAtual - 1}
              onPageChange={(e, newPage) => setPaginaAtual(newPage + 1)}
              rowsPerPage={itensPorPagina}
              rowsPerPageOptions={[5, 10, 25]}
              onRowsPerPageChange={() => {}}
              labelRowsPerPage="Status por página:"
              sx={{
                color: "var(--ig-t-ffffff)",

                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 0,
                  paddingRight: 0,
                  minHeight: 58,
                },

                "& .MuiTablePagination-selectLabel": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiTablePagination-displayedRows": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiSvgIcon-root": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiIconButton-root": {
                  color: "var(--ig-t-ffffff)",
                },

                "& .MuiSelect-select": {
                  color: "var(--ig-t-ffffff)",
                },
              }}
            />
          </div>
        )}

        {/* AVISO DE PERMISSÃO */}
        {!podeCadastrar && (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você possui acesso apenas de visualização.</p>
          </div>
        )}

        {/* FORMULÁRIO */}
        {podeCadastrar && (
          <div className="bloco-formulario">
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <TextField
                id="status"
                label="Status"
                value={novoStatus}
                onChange={(e) => setNovoStatus(e.target.value.toUpperCase())}
                placeholder="Digite o nome do Status"
                fullWidth
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 56,
                    display: "flex",
                    alignItems: "center",
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16.5px 14px",
                  },
                  "& .MuiInputLabel-root": {
                    transform: "translate(14px, 16px) scale(1)",
                  },
                  "& .MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                startIcon={<i className="fas fa-floppy-disk" />}
                sx={{
                  width: 240,
                  minWidth: 240,
                  maxWidth: 240,

                  height: 52,
                  minHeight: 52,

                  alignSelf: "center",

                  textTransform: "none",
                  fontWeight: 600,

                  color: "#fff",
                  backgroundColor: "#1b5e20",

                  "&:hover": {
                    backgroundColor: "#1b5e20",
                  },
                }}
              >
                Cadastrar
              </Button>
            </Box>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default Status;
